/**
 * Ziele einer Drückjagd je Rolle (oberflaeche.md §3.1 „Ziele der Startansicht“, §3.2, §4.3): Kernaktionen weiterer
 * Rollen stehen immer direkt, von den übrigen Zielen höchstens vier in der Priorität der Rolle, alle übrigen in der
 * Ansicht „mehr“. Pfade relativ zu `/drueckjagd/<id>`; Kanäle und Meldungen liegen außerhalb.
 */
import type { Kernaktion, Startbeitrag } from "../plattform/startansicht";

export interface Ziel {
  begriff: string;
  symbol: string;
  pfad: (id: string) => string;
}

const z = (begriff: string, symbol: string, teil: string): Ziel => ({ begriff, symbol, pfad: (id) => `/drueckjagd/${id}/${teil}` });

export const ZIELE: Record<string, Ziel> = {
  Planung: z("Planung", "einstellungen", "planung"),
  Lagebild: z("Lagebild", "lagebild", "lagebild"),
  Karte: z("Karte", "karte", "karte"),
  Einweisung: z("Einweisung", "einweisung", "einweisung"),
  "Meine Aufgaben": z("Meine Aufgaben", "aufgaben", "aufgaben"),
  Notfallinformationen: z("Notfallinformationen", "rettungspunkt", "notfallinformationen"),
  Revierkarte: z("Revierkarte", "karte", "revierkarte"),
  Standbesetzung: z("Standbesetzung", "stand", "standbesetzung"),
  Standansicht: z("Standansicht", "stand", "standansicht"),
  "Hunde im Treiben": z("Hunde im Treiben", "hund", "hunde"),
  Freigabe: z("Freigabe", "freigabe", "freigabe"),
  Abholliste: z("Abholliste", "abgeholt", "abholliste"),
  Bergeliste: z("Bergeliste", "bergung", "bergeliste"),
  "Offene Suchen": z("Offene Suchen", "nachsuche", "offene-suchen"),
  Strecke: z("Strecke", "strecke", "strecke"),
  Jagdstatistik: z("Jagdstatistik", "strecke", "jagdstatistik"),
  Kontrollliste: z("Kontrollliste", "kontrolle", "kontrollliste"),
  Kanäle: { begriff: "Kanäle", symbol: "nachricht", pfad: (id) => `/kanaele?drueckjagd=${id}` },
  Meldungen: z("Meldungen", "warteschlange", "meldungen"),
};

/**
 * Weitere Ziele je Rolle in absteigender Priorität (design/README §10.2: Priorität 1 ist für Schütze, Treiber und
 * Hundeführer „Kanäle“, für die Leitung „Offene Suchen“, für den Nachweisprüfer „Kanäle“; Referenz `#s-start`,
 * `#s-leitung`). „Meldungen“ steht vorn, solange etwas nicht übertragen ist (Startansicht).
 */
const JE_ROLLE: Record<string, string[]> = {
  Jagdherr: ["Offene Suchen", "Strecke", "Kanäle", "Planung", "Freigabe", "Standbesetzung", "Jagdstatistik", "Karte", "Lagebild"],
  Jagdleiter: ["Offene Suchen", "Strecke", "Kanäle", "Planung", "Freigabe", "Standbesetzung", "Karte", "Lagebild"],
  Schütze: ["Kanäle", "Revierkarte", "Einweisung", "Freigabe", "Meine Aufgaben", "Standansicht", "Standbesetzung"],
  Standschnaller: ["Kanäle", "Revierkarte", "Einweisung", "Freigabe", "Hunde im Treiben"],
  Ansteller: ["Kanäle", "Revierkarte", "Einweisung", "Abholliste", "Standbesetzung", "Bergeliste", "Freigabe"],
  Treiber: ["Kanäle", "Einweisung", "Revierkarte", "Meine Aufgaben", "Hunde im Treiben"],
  Treiberführer: ["Kanäle", "Einweisung", "Revierkarte", "Meine Aufgaben", "Hunde im Treiben"],
  "Durchgeh-Hundeführer": ["Kanäle", "Hunde im Treiben", "Revierkarte", "Meine Aufgaben", "Einweisung"],
  Nachsuchenführer: ["Kanäle", "Hunde im Treiben", "Offene Suchen", "Revierkarte", "Einweisung"],
  Nachweisprüfer: ["Kanäle", "Einweisung", "Revierkarte"],
  Bergetrupp: ["Kanäle", "Revierkarte", "Einweisung", "Bergeliste"],
  Wildversorgung: ["Kanäle", "Revierkarte", "Einweisung", "Strecke"],
};

const ALLGEMEIN = ["Einweisung", "Meine Aufgaben", "Notfallinformationen", "Revierkarte", "Hunde im Treiben", "Kanäle", "Meldungen"];

/** Kernaktionen, die diese Startansicht als Ziel führt (die übrigen stehen in den Kontexten Lage und Schuss). */
export function kernziele(rollen: readonly string[]): Ziel[] {
  return rollen.some((r) => r === "Jagdherr" || r === "Jagdleiter" || r === "Nachweisprüfer") ? [ZIELE["Kontrollliste"]!] : [];
}

export function weitereZiele(rollen: readonly string[]): Ziel[] {
  const namen = [...new Set([...rollen.flatMap((r) => JE_ROLLE[r] ?? []), ...ALLGEMEIN])];
  return namen.map((n) => ZIELE[n]!).filter(Boolean);
}

/** Ziel auf der Startansicht bzw. in „mehr“ mit fertigem Pfad. */
export interface Startziel {
  begriff: string;
  symbol: string;
  ziel: string;
  ausloesen?: () => void;
}

/**
 * Ziele der Startansicht aus den Beiträgen der Kontexte (plattform/startansicht.ts) und den Zielen der Rolle: die
 * Hauptaktion (kleinste Zeile in §4.3), die übrigen Kernaktionen (immer direkt) und alle weiteren Ziele in ihrer
 * Reihenfolge — dringende der Kontexte vorn, dann die der Rolle (design/README §10.2), dann die übrigen; ohne Doppel und
 * ohne die Hauptaktion. Davon stehen höchstens vier direkt, die übrigen in „mehr“ (§3.1 V-12).
 */
export function zieleDerStartansicht(beitraege: readonly Startbeitrag[], rollen: readonly string[], id: string) {
  const kernaktionen = beitraege.flatMap((b) => b.kernaktionen);
  const bereit = beitraege.every((b) => b.bereit !== false);
  // Suchbereich (§4.4) in `main`: er ist die Hauptaktion; eine Ansicht als Hauptaktion („Hunde im Treiben“) tritt zurück
  const suchbereich = beitraege.some((b) => !!b.bereiche);
  // Hauptaktion: die der zuerst genannten Zeile in §4.3; ein Rückfall nur, wenn keine Zeile eine eigentliche hat
  const haupt: Kernaktion | undefined = kernaktionen.filter((k) => k.haupt && (bereit || !k.rueckfall) && !(suchbereich && k.nurAlsHaupt))
    .sort((a, b) => Number(!!a.rueckfall) - Number(!!b.rueckfall) || a.rang - b.rang)[0];
  const neben: Startziel[] = kernaktionen.filter((k) => k !== haupt && !k.weiteresZiel && !k.nurAlsHaupt)
    .map((k) => ({ begriff: k.begriff, symbol: k.symbol, ziel: k.pfad, ...(k.ausloesen ? { ausloesen: k.ausloesen } : {}) }));
  const kern: Startziel[] = kernziele(rollen).filter((z) => z.begriff !== haupt?.begriff)
    .map((z) => ({ begriff: z.begriff, symbol: z.symbol, ziel: z.pfad(id) }));
  const beitragsziele = beitraege.flatMap((b) => b.weitere ?? []);
  const alsZiel = (z: { begriff: string; symbol: string; pfad: string }): Startziel => ({ begriff: z.begriff, symbol: z.symbol, ziel: z.pfad });
  const kandidaten: Startziel[] = [
    ...beitragsziele.filter((z) => z.vorn).map(alsZiel),
    ...kernaktionen.filter((k) => k !== haupt && k.weiteresZiel && !k.nurAlsHaupt).map(alsZiel),
    ...weitereZiele(rollen).map((z) => ({ begriff: z.begriff, symbol: z.symbol, ziel: z.pfad(id) })),
    ...beitragsziele.filter((z) => !z.vorn).map(alsZiel),
  ];
  const belegt = new Set([haupt?.begriff, ...neben.map((z) => z.begriff), ...kern.map((z) => z.begriff)]);
  const weitere = kandidaten.filter((z) => !belegt.has(z.begriff) && (belegt.add(z.begriff), true));
  return { haupt, neben, kern, weitere };
}

/**
 * Kernaktionen ohne meldende Handlung (Ansichten; Anforderungen K105, Präzisierung zu V-12; oberflaeche.md §3.1 V-29): Nur sie
 * dürfen hinter „mehr“ rücken, wenn die eine Reihe der Sekundärziele nicht für jede Kernaktion weiterer Rollen reicht.
 */
export const KERNAKTIONEN_OHNE_MELDUNG: ReadonlySet<string> = new Set(["Kontrollliste", "Lagebild", "Abholliste", "Bergeliste"]);

/** Kernaktion weiterer Rollen in der Reihe der Sekundärziele mit ihrem Platz (`data-platz`) und ihrer Art (`data-meldend`). */
export interface Reihenziel extends Startziel {
  platz: number;
  meldend: boolean;
}

/**
 * Kernaktionen weiterer Rollen in der einen Reihe der Sekundärziele (K105, V-29): bei einem Suchbereich (§4.4) die
 * Hauptaktion, die übrigen Kernaktionen der Kontexte und die der Rolle. Meldende stehen vorn und immer direkt (notfalls in einer
 * vierten kompakten Spalte); die ohne meldende Handlung folgen und stehen nur, solange die Reihe Platz hat (app.css nach
 * `data-platz`), sonst zuerst in „mehr“ (`zieleInMehr`).
 */
export function kernaktionenDerReihe(ziele: Pick<ReturnType<typeof zieleDerStartansicht>, "haupt" | "neben" | "kern">, suche: boolean): Reihenziel[] {
  const { haupt } = ziele;
  const hauptInReihe: Startziel[] = suche && haupt ? [{ begriff: haupt.begriff, symbol: haupt.symbol, ziel: haupt.pfad, ...(haupt.ausloesen ? { ausloesen: haupt.ausloesen } : {}) }] : [];
  const alle = [...hauptInReihe, ...ziele.neben, ...ziele.kern];
  const meldend = (z: Startziel) => !KERNAKTIONEN_OHNE_MELDUNG.has(z.begriff);
  return [...alle.filter(meldend), ...alle.filter((z) => !meldend(z))].map((z, i) => ({ ...z, platz: i + 1, meldend: meldend(z) }));
}

/**
 * Ziele der Ansicht „mehr“ (§3.2, V-12, V-29): zuerst die Kernaktionen der Reihe ohne meldende Handlung (sie können in der
 * Reihe fehlen), dann alle weiteren Ziele. Meldende Kernaktionen stehen nie nur hier, sondern immer direkt.
 */
export function zieleInMehr(ziele: ReturnType<typeof zieleDerStartansicht>, suche: boolean): Startziel[] {
  return [...kernaktionenDerReihe(ziele, suche).filter((z) => !z.meldend), ...ziele.weitere];
}
